import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Company} from '../../../../models/company';
import {User} from '../../../../models/user';
import {DomainField, DomainRecord} from '../../../../models/domain';
import {DomainService} from '../../../../services/domain/domain.service';
import {promptCreateDomain} from '../../../admin-domains/create-domain-dialog';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-companies-settings-extras',
    templateUrl: './companies-settings-extras.component.html',
    styles: [':host { display: contents !important; }']
})
export class CompaniesSettingsExtrasComponent implements OnChanges {
    @Input() company: Company = new Company();
    @Input() agents: User[] = [];
    @Input() hasGlobalVisibility = false;
    @Input() companyHasShop = false;
    @Input() isSuperAdmin = false;
    @Input() rulesUrl = '';
    @Input() signatureUrl = '';

    @Output() rulesChange = new EventEmitter<Event>();
    @Output() signatureChange = new EventEmitter<Event>();

    domains: DomainRecord[] = [];
    loadingDomains = false;
    savingDomain = false;
    private loadedCompanyId?: string;

    constructor(private domainService: DomainService) {}

    get showShopPolicy(): boolean {
        return this.companyHasShop || this.isSuperAdmin;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.isSuperAdmin && this.company?.id && this.company.id !== this.loadedCompanyId) {
            this.loadedCompanyId = this.company.id;
            void this.loadDomains();
        }
    }

    async loadDomains() {
        if (!this.company?.id) return;
        this.loadingDomains = true;
        const response = await this.domainService.getCompanyDomains(this.company.id);
        this.domains = response.success && Array.isArray(response.data) ? response.data : [];
        this.loadingDomains = false;
    }

    async createRecord(field: DomainField = 'domain') {
        if (!this.company?.id || this.savingDomain) return;

        const values = await promptCreateDomain({
            name: this.company[field] || this.company.domain,
            field
        });
        if (!values) return;

        this.savingDomain = true;
        const response = await this.domainService.addDomain(this.company.id, { name: values.name });
        this.savingDomain = false;

        if (!response.success || !response.data?.length) {
            await Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: this.domainService.domainErrorMessage(response, 'Impossibile creare il record DNS')
            });
            return;
        }

        for (const record of response.data) {
            this.company[record.field] = record.name;
        }
        await this.loadDomains();
        const createdShop = response.data.some((record) => record.field === 'shopDomain');
        const createdCompany = response.data.some((record) => record.field === 'domain');
        await Swal.fire({
            icon: 'success',
            title: 'Perfetto!',
            text: createdCompany && createdShop
                ? 'Record DNS società e shop creati correttamente'
                : createdShop
                    ? 'Record DNS shop creato correttamente'
                    : 'Record DNS società creato correttamente'
        });
    }

    async deleteRecord(domain: DomainRecord) {
        if (!this.company?.id) return;

        const confirmed = await Swal.fire({
            icon: 'warning',
            title: 'Eliminare il record DNS?',
            text: domain.name,
            showCancelButton: true,
            confirmButtonText: 'Si, elimina',
            cancelButtonText: 'Annulla'
        });
        if (!confirmed.isConfirmed) return;

        const response = await this.domainService.removeDomain({
            companyId: this.company.id,
            recordId: domain.recordId,
            field: domain.field
        });

        if (!response.success) {
            await Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: this.domainService.domainErrorMessage(response, 'Impossibile eliminare il record DNS')
            });
            return;
        }

        this.company[domain.field] = '';
        await this.loadDomains();
        await Swal.fire({
            icon: 'success',
            title: 'Perfetto!',
            text: 'Record DNS eliminato'
        });
    }

    fieldLabel(field?: DomainField) {
        return field === 'shopDomain' ? 'Shop' : 'Società';
    }
}
